import type { Node as ProseNode } from '@tiptap/pm/model';

/**
 * Between a caret in PLAIN TEXT and a position in the document.
 *
 * The field's value is a string and everything around the editor counts in it:
 * the phrase library splices at a character offset, the counter counts
 * characters, `maxLength` clamps characters. ProseMirror counts positions,
 * which are not the same number — every paragraph costs one for its opening
 * token and one for its closing one.
 *
 * The rule here is the one `getText({ blockSeparator: '\n' })` uses: the
 * blocks' texts joined with a newline. Both directions are pure and tested,
 * because a caret landing one character off is the kind of defect nobody sees
 * until a Textbaustein overwrites the wrong letter.
 */

/** The document position for a character offset into the plain text. */
export function posForOffset(doc: ProseNode, offset: number): number {
    let remaining = Math.max(0, offset);
    // Position 0 is before the first block; +1 steps inside one.
    let pos = 0;
    for (let index = 0; index < doc.childCount; index++) {
        const block = doc.child(index);
        const length = block.textContent.length;
        if (remaining <= length) {
            return pos + 1 + remaining;
        }
        // `+ 1` for the newline this block contributes to the plain text.
        remaining -= length + 1;
        pos += block.nodeSize;
    }
    // Past the end — clamp to the last position inside the last block.
    return Math.max(1, doc.content.size - 1);
}

/** The character offset into the plain text for a document position. */
export function offsetForPos(doc: ProseNode, pos: number): number {
    let offset = 0;
    let seen = 0;
    for (let index = 0; index < doc.childCount; index++) {
        const block = doc.child(index);
        const length = block.textContent.length;
        // Inside this block: `seen + 1` is the position of its first character.
        if (pos <= seen + block.nodeSize - 1) {
            return offset + Math.max(0, Math.min(length, pos - seen - 1));
        }
        offset += length + 1;
        seen += block.nodeSize;
    }
    return Math.max(0, offset - 1);
}
